/**
 * Auth State Manager
 * 
 * Manages authentication state in a way that survives React component remounts
 * during navigation while maintaining security.
 */

import { User } from '../context/AuthContext';
import { enhancedTokenManager } from '../services/EnhancedTokenManager';

class AuthStateManager {
  private static instance: AuthStateManager;
  private user: User | null = null;
  private listeners: Set<(user: User | null) => void> = new Set();

  private constructor() {
    // Initialize from session storage if available
    this.initializeFromSession();
  }

  static getInstance(): AuthStateManager {
    if (!AuthStateManager.instance) {
      AuthStateManager.instance = new AuthStateManager();
      // Also attach to window for persistence
      (window as any).__authStateManager = AuthStateManager.instance;
    }
    return (window as any).__authStateManager || AuthStateManager.instance;
  }

  private initializeFromSession() {
    const hasActiveSession = sessionStorage.getItem('connectpath_active_session') === 'true';
    const storedUser = enhancedTokenManager.getUserData();
    
    if (hasActiveSession && storedUser) {
      this.user = storedUser;
      console.log('[AuthStateManager] Initialized user from session');
    }
  }

  getUser(): User | null {
    return this.user;
  }

  setUser(user: User | null) {
    console.log('[AuthStateManager] setUser called with:', user?.preferences);
    this.user = user;

    if (user) {
      // Store using EnhancedTokenManager and mark session as active
      console.log('[AuthStateManager] Storing user data with preferences:', user.preferences);
      enhancedTokenManager.setUserData(user);
      sessionStorage.setItem('connectpath_active_session', 'true');
    } else {
      // Clear storage
      enhancedTokenManager.clearUserData();
      sessionStorage.removeItem('connectpath_active_session');
    }

    // Notify all listeners
    this.listeners.forEach(listener => listener(user));
  }
  
  clearUser() {
    this.setUser(null);
  }

  subscribe(listener: (user: User | null) => void) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  hasActiveSession(): boolean {
    return sessionStorage.getItem('connectpath_active_session') === 'true' && !!this.user;
  }
}

export const authStateManager = AuthStateManager.getInstance();